import React, { Dispatch, SetStateAction, useEffect } from 'react';
import {
  Button,
  Checkbox,
  Message,
  Table,
  TagPicker,
  useToaster,
} from 'rsuite';
import * as requestsApi from '../../api/requests';
import CheckIcon from '@rsuite/icons/Check';
import DeleteIcon from '@rsuite/icons/Trash';
import { FormModal } from '../../common/FormModal';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import { useConfirm } from '../../common/useConfirm';

type Props = {
  requestId: string;
  setRequestId: Dispatch<SetStateAction<string>>;
  coursesData: { value: string; label: string }[];
};

const { Column, HeaderCell, Cell } = Table;

export default function RequestQueuesModal({
  requestId,
  setRequestId,
  coursesData,
}: Props) {
  let cellClicked = false;
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [alreadyJoinedQueues, setAlreadyJoinedQueues] = React.useState<any[]>(
    []
  );
  const [selectedCoursesIds, setSelectedCoursesIds] = React.useState<string[]>(
    []
  );
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = React.useState<boolean>(false);

  const getRequestQueueEntries = async () => {
    setLoading(true);
    try {
      const response = await requestsApi.getQueueEntries(
        requestId,
        'createdAt',
        sortOrder.toUpperCase()
      );
      setAlreadyJoinedQueues(response.queue);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (requestId) {
      getRequestQueueEntries().catch(console.error);
    }
  }, [requestId, sortOrder]);

  const selection = ({ courseId }: { courseId: string }) => {
    if (selectedRows.includes(courseId)) {
      setSelectedRows(selectedRows.filter(r => r !== courseId));
    } else {
      setSelectedRows([...selectedRows, courseId]);
    }
  };
  return (
    <>
      {ConfirmDialog}
      <FormModal
        open={requestId.length > 0}
        onClose={() => {
          setRequestId('');
          setSelectedCoursesIds([]);
        }}
        title="Черги курсів анкети"
        footer={
          <>
            <Button
              onClick={async () => {
                if (!selectedCoursesIds.length) {
                  setRequestId('');
                  setSelectedCoursesIds([]);
                  setAlreadyJoinedQueues([]);
                  return;
                }
                try {
                  await requestsApi.addToCoursesQueues(
                    requestId,
                    selectedCoursesIds
                  );
                  toaster.push(
                    <Message type="success" showIcon closable>
                      Черги оновлено
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                  setRequestId('');
                  setSelectedCoursesIds([]);
                  setAlreadyJoinedQueues([]);
                } catch (e: any) {
                  const errorMessage =
                    e.response?.data?.message ||
                    'Не вдалося додати у чергу. Спробуйте пізніше';
                  toaster.push(
                    <Message type="error" showIcon closable>
                      {errorMessage}
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                }
              }}
              appearance="primary"
            >
              Додати
            </Button>
            <Button
              onClick={() => {
                setRequestId('');
                setSelectedCoursesIds([]);
              }}
              appearance="subtle"
            >
              Скасувати
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex' }}>
          <div>
            <label>Додати до черги курсів</label>
            <br />
            <TagPicker
              data={coursesData.filter(
                cD =>
                  !alreadyJoinedQueues.find(aJQ => aJQ.courseId === cD.value)
              )}
              onChange={setSelectedCoursesIds}
            />
            <Button
              style={{ width: 32, height: 32, alignSelf: 'end', margin: 10 }}
              onClick={async () => {
                if (!selectedCoursesIds.length) {
                  return;
                }
                try {
                  await requestsApi.addToCoursesQueues(
                    requestId,
                    selectedCoursesIds
                  );
                  toaster.push(
                    <Message type="success" showIcon closable>
                      Черги оновлено
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                  setSelectedCoursesIds([]);
                  await getRequestQueueEntries().catch(console.error);
                } catch (e: any) {
                  const errorMessage =
                    e.response?.data?.message ||
                    'Не вдалося додати у чергу. Спробуйте пізніше';
                  toaster.push(
                    <Message type="error" showIcon closable>
                      {errorMessage}
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                }
              }}
            >
              <CheckIcon />
            </Button>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            {selectedRows.length > 0 && (
              <Button
                onClick={async () => {
                  const result = await confirm({
                    message: `Ви впевнені, що хочете видалити анкету з черг ${selectedRows.length} курсів?`,
                    destructive: true,
                    confirmText: 'Видалити',
                  });
                  if (!result) {
                    return;
                  }
                  try {
                    await requestsApi.removeFromCoursesQueues(
                      requestId,
                      selectedRows
                    );
                    toaster.push(
                      <Message type="success" showIcon closable>
                        Студента видалено з черг
                        {selectedRows.length === 1 ? 'и' : ''}
                      </Message>,
                      {
                        placement: 'topEnd',
                        duration: 1500,
                      }
                    );
                    setSelectedRows([]);
                    getRequestQueueEntries().catch(console.error);
                  } catch (err: any) {
                    console.error(err);
                    const errorMessage =
                      err.response?.data?.message ||
                      'Не вдалося оновити чергу. Спробуйте пізніше';
                    toaster.push(
                      <Message type="error" showIcon closable>
                        {errorMessage}
                      </Message>,
                      {
                        placement: 'topEnd',
                        duration: 1500,
                      }
                    );
                  }
                }}
              >
                Видалити з черг{selectedRows.length === 1 ? 'и' : ''}
              </Button>
            )}
          </div>
        </div>
        <Table
          loading={loading}
          sortColumn={'createdAt'}
          sortType={sortOrder}
          autoHeight={true}
          onSortColumn={() => {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
          }}
          renderLoading={renderTableLoading}
          renderEmpty={renderTableEmpty}
          data={alreadyJoinedQueues}
        >
          <Column width={64}>
            <HeaderCell>
              <Button
                appearance="link"
                onClick={() => {
                  if (selectedRows.length === alreadyJoinedQueues.length) {
                    setSelectedRows([]);
                  } else {
                    setSelectedRows(alreadyJoinedQueues.map(q => q.courseId));
                  }
                }}
              >
                <CheckIcon />
              </Button>
            </HeaderCell>
            <Cell style={{ display: 'flex', alignItems: 'center' }}>
              {rowData => (
                <Checkbox
                  checked={selectedRows.includes(rowData.courseId)}
                  onCheckboxClick={() => {
                    selection({ courseId: rowData.courseId });
                  }}
                />
              )}
            </Cell>
          </Column>
          <Column width={320} resizable={true} fullText={true}>
            <HeaderCell>Курс</HeaderCell>
            <Cell dataKey="course.name" />
          </Column>
          <Column width={200} sortable={true} resizable={true} fullText={true}>
            <HeaderCell>Очікує з</HeaderCell>
            <Cell dataKey="createdAt">
              {rowData => new Date(rowData.createdAt).toLocaleString()}
            </Cell>
          </Column>

          <Column width={80} resizable={true}>
            <HeaderCell>Дії</HeaderCell>
            <Cell>
              {rowData => (
                <>
                  <Button
                    appearance="subtle"
                    onClick={async () => {
                      cellClicked = true;
                      const result = await confirm({
                        message: `Ви впевнені, що хочете видалити анкету з черги ${rowData.course.name}?`,
                        destructive: true,
                        confirmText: 'Видалити',
                      });
                      if (!result) {
                        return;
                      }
                      try {
                        await requestsApi.removeFromCoursesQueues(requestId, [
                          rowData.courseId,
                        ]);
                        toaster.push(
                          <Message type="success" showIcon closable>
                            Студента видалено з черги
                          </Message>,
                          {
                            placement: 'topEnd',
                            duration: 1500,
                          }
                        );
                        getRequestQueueEntries().catch(console.error);
                      } catch (err: any) {
                        console.error(err);
                        const errorMessage =
                          err.response?.data?.message ||
                          'Не вдалося оновити чергу. Спробуйте пізніше';
                        toaster.push(
                          <Message type="error" showIcon closable>
                            {errorMessage}
                          </Message>,
                          {
                            placement: 'topEnd',
                            duration: 1500,
                          }
                        );
                      }
                    }}
                  >
                    <DeleteIcon />
                  </Button>
                </>
              )}
            </Cell>
          </Column>
        </Table>
        {selectedRows.length ? <p>Виділено: {selectedRows.length}</p> : <></>}
        {/*<SelectPicker onSelect={value => {*/}
        {/*  setSelectedCoursesIds(value as string);*/}
        {/*}} searchable={false} placeholder="Оберіть курс" data={coursesData}/>*/}
      </FormModal>
    </>
  );
}
